import OutputLocationTab from './OutputLocationTab';
import { TabMethods } from './models';
import { OutputLocationsInputProps } from '../common';
import { buildFileNamesTemplate, FileNamesTemplate } from '../../../common';
import { ProgramWizardStepDataProcessor } from '../common/ProgramWizardStepDataProcessor';
import { arrayUtils } from '@/app/core/utils';
import { useDataPart, useStepContext } from '@/app/core/components/step-wizard';
import { useProgramDependentDataContext } from '@/app/core/components/program-wizard/program-dependent-data-context';
import { useState, useRef, useEffect, ReactNode } from 'react';
import { Tabs, Card, Alert } from '@ssar-app/uikit';
import { OutputLocation, OutputLocationsInformation } from '@ssar-app/domain';
import { useForm, useFieldArray } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers';
import { CSSObject, Icon } from '@chakra-ui/react';
import {
  AiOutlineCheckCircle,
  AiOutlineCloseCircle,
} from 'react-icons/ai/index.js';
import * as yup from 'yup';

const { replaceIndex } = arrayUtils;

export const OutputLocations = (props: OutputLocationsInputProps) => {
  const { stepKey, validator } = props;
  const { clientAttributes } = props.moduleProps;
  const { isStepActive } = useStepContext();
  const { updateWarnings, setUpdateWarnings } =
    useProgramDependentDataContext();
  const [dataPartState] = useDataPart<OutputLocationsInformation>(stepKey);
  const [outputLocationsData, setOutputLocationsData] =
    useState<OutputLocationsInformation>(
      (dataPartState.data as OutputLocationsInformation) || [],
    );
  const outputLocationsValidator = yup.object({
    outputLocations: validator,
  });
  const formMethods = useForm<{ outputLocations: OutputLocationsInformation }>({
    defaultValues: {
      outputLocations: outputLocationsData || [],
    },
    resolver: yupResolver(outputLocationsValidator),
    mode: 'onChange',
  });
  const { errors, control, formState, setValue, trigger, register } =
    formMethods;

  const programKey = (useDataPart(['programKey'])?.[0]?.data as string) || '';
  const tabsRefs = useRef<(TabMethods | null)[]>([]);
  const fileNamesTemplateRef = useRef<FileNamesTemplate>(
    buildFileNamesTemplate(programKey),
  );
  const getValidTabsRefs = (): TabMethods[] =>
    (tabsRefs.current || []).filter(
      (a) => a !== null && a !== undefined,
    ) as TabMethods[];
  const [tabsValidStatuses, setTabsValidStatuses] = useState<
    (boolean | undefined)[]
  >([]);

  const { fields, remove, append } = useFieldArray<OutputLocation>({
    control,
    name: 'outputLocations',
    keyName: 'id',
  });

  const updateTabStatuses = () => {
    Promise.all(getValidTabsRefs().map((a) => a?.isValid())).then((values) => {
      setTabsValidStatuses(values);
    });
  };

  useEffect(() => {
    updateTabStatuses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outputLocationsData]);

  useEffect(() => {
    register('outputFiles');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // added this to set proper output location objects after deleting the tab.
  useEffect(() => {
    setValue('outputLocations', getStepData());
    validateContactSelection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields]);

  const [activeTabIndex, setActiveTabIndex] = useState<number>(0);

  const onTabEdit = (action: 'add' | 'remove', targetIndex?: string): void => {
    if (action === 'add') {
      append({});
    } else if (action === 'remove') {
      const removeIndex = Number(targetIndex);
      tabsRefs.current = tabsRefs.current.filter(
        (_, index) => index !== removeIndex,
      );
      setTabsValidStatuses(
        tabsValidStatuses.filter((_, index) => index !== removeIndex),
      );
      setActiveTabIndex(0);
      remove(removeIndex);

      const newOutputLocations = [...outputLocationsData];
      newOutputLocations.splice(removeIndex);
      setOutputLocationsData(newOutputLocations);
      setValue('outputLocations', newOutputLocations);
    }
  };
  // console.log('Suite: ', tabsRefs, tabsValidStatuses);

  const replaceTabValidStatus = (
    index: number,
    isValid: boolean | undefined,
  ): void => {
    const newStatuses = replaceIndex(tabsValidStatuses, index, isValid);
    setTabsValidStatuses(newStatuses);
  };

  const onTabChange = (tabIndex: string): void => {
    const prevTabIndex = activeTabIndex;
    const prevTab = tabsRefs.current[prevTabIndex];
    if (prevTab) {
      prevTab
        .isValid()
        .then((a) => {
          replaceTabValidStatus(prevTabIndex, a);
        })
        .catch((err) => {
          console.error(err);
          replaceTabValidStatus(prevTabIndex, false);
        })
        .finally(() => {
          setActiveTabIndex(Number(tabIndex));
        });
    } else {
      setActiveTabIndex(Number(tabIndex));
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getStepData = (): any[] => {
    const toReturn = getValidTabsRefs().map((a) => a?.getTabData());
    return toReturn;
  };

  const validateFilesSelection = (): Promise<boolean> => {
    return trigger('outputFiles')
      .then((a) => a)
      .catch((err) => {
        console.error(err);
        return false;
      });
  };

  const validateContactSelection = (): void => {
    setUpdateWarnings(!updateWarnings);
  };

  useEffect(() => {
    if (fields.length === 0) {
      append({});
    } else {
      validateContactSelection();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const anyErrors = (errors as any)?.outputFiles;

  const updateOutputLocations = (
    outputLocation: OutputLocation,
    index: number,
  ) => {
    const newOutputLocations = [...outputLocationsData];
    newOutputLocations[index] = outputLocation;
    setOutputLocationsData(newOutputLocations);
    setValue('outputLocations', newOutputLocations);
    validateFilesSelection();
  };

  return (
    <>
      <ProgramWizardStepDataProcessor
        stepKey={stepKey}
        trigger={trigger}
        triggerFieldNames={['outputLocations']}
        validator={validator as yup.ArraySchema<OutputLocation>}
        model={outputLocationsData}
        isDirty={formState.isDirty}
      />
      {isStepActive && (
        <Card mb={10}>
          {anyErrors?.message && (
            <Alert
              isOpen
              type='error'
              description={anyErrors?.message}
              showCloseButton={false}
              chakraAlertProps={{ mb: 4 }}
            />
          )}
          <Tabs
            index={activeTabIndex < fields.length ? activeTabIndex : 0}
            items={fields.map((field, index) => {
              const tabStatus = tabsValidStatuses[index];
              const tabIcon =
                fields[activeTabIndex < fields.length ? activeTabIndex : 0]
                  .id === field.id
                  ? null
                  : getTabIcon(tabStatus);

              return {
                content: (
                  <OutputLocationTab
                    fileNamesTemplate={fileNamesTemplateRef.current}
                    onFileSelectionChanged={(
                      outputLocation: OutputLocation,
                    ) => {
                      updateOutputLocations(outputLocation, index);
                    }}
                    // onOutputContactSelected={onOutputContactSelected}
                    onContactSelected={(outputLocation: OutputLocation) => {
                      updateOutputLocations(outputLocation, index);
                    }}
                    ref={(e) => {
                      tabsRefs.current[index] = e;
                    }}
                    clientAttributes={clientAttributes}
                    model={
                      {
                        ...field,
                        ...outputLocationsData?.[index],
                      } as OutputLocation
                    }
                    onChange={(outputLocation: OutputLocation) => {
                      updateOutputLocations(outputLocation, index);
                    }}
                  />
                ),
                label: (
                  <>
                    {tabIcon}
                    {`Output Location ${index + 1}`}
                  </>
                ),
                key: `${index}`,
                closable: index > 0,
              };
            })}
            variant='enclosed-colored'
            type='editable'
            tabListProps={{ gap: 2 }}
            onChange={onTabChange}
            onEdit={onTabEdit}
            hideAdd={fields.length >= 3}
          />
        </Card>
      )}
    </>
  );
};

const getTabIcon = (status: boolean | undefined): ReactNode => {
  const styles: CSSObject = { mr: 1 };
  if (status === true) {
    return (
      <Icon as={AiOutlineCheckCircle} color='green.500' sx={{ ...styles }} />
    );
  } else if (status === false) {
    return (
      <Icon as={AiOutlineCloseCircle} color='red.500' sx={{ ...styles }} />
    );
  } else {
    return null;
  }
};
